import {
  WIZARD_DRAFT_KEY,
  applyIntakeRecommendations,
  buildTemplateBundle,
  countGeneratedContent,
  createWizardDraft,
  loadWizardDraft,
  newWizardLogo,
  newWizardPage,
  newWizardPlan,
  newWizardStory,
  newWizardTestimonial,
  saveWizardDraft,
  splitWizardLines,
} from './wizard-state.js';

// Reusing helpers injected by host (no internal side effects expected)
export async function renderHosting(state, helpers) {
  const { api, t, showNotice, escapeHtml, navigate, refreshMeta, brandOptions, setActiveBrand, startJob, heading } = helpers;
  const content = document.querySelector('#content');
  if (!content) return;

  try {
    const brandId = state.brandId || '';
    const res = await api('/api/hosting/overview?brand=' + encodeURIComponent(brandId));
    const { brand, pages, stats } = res;

    const statusOptions = ['All', 'Published', 'Ready', 'Planned'];
    const statusBadgeClass = (status) => {
      switch (status) {
        case 'Published': return { status: 'success', label: t('Yayınlandı', 'Published') };
        case 'Ready': return { status: 'success', label: t('Hazır', 'Ready') };
        case 'Planned': return { status: 'warning', label: t('Planlandı', 'Planned') };
        default: return { status: 'muted', label: t('Bilinmiyor', 'Unknown') };
      }
    };

    // Compute effective base URL for tenant routing panel
    const primaryDomain = (brand?.routing?.primaryDomain || '').replace(/^https?:\/\//, '');
    const basePath = brand?.routing?.basePath || '/lp';
    const effectiveBaseUrl = `https://${primaryDomain}${basePath}/`;

    content.innerHTML = `
      ${heading(t('Hosting ve Tenant Yönetimi', 'Hosting & Tenant Management'), t('Çoklu tenant ve Azure rotayı yönetin.', 'Manage multi-tenant Azure routing.'), '<fluent-button id="rebuild-tenant" appearance="accent">' + t('Tenant\'ı Yeniden Oluştur / Yayınla', 'Rebuild / Publish Tenant') + '</fluent-button>')}

      <section class="metric-grid" aria-label="${t('Tenant özeti', 'Tenant summary')}">
        <article class="metric"><span>${t('Toplam Landing Sayfası', 'Total Landing Pages')}</span><strong>${stats.totalPages || 0}</strong><small>${t('tüm tenant kümeleri', 'all tenant clusters')}</small></article>
        <article class="metric"><span>${t('Yayınlanan (Canlı)', 'Published (Live)')}</span><strong>${stats.published || 0}</strong><small>${t('Canlıda çalışan', 'currently live')}</small></article>
        <article class="metric"><span>${t('Hazır (Taslak)', 'Ready (Draft)')}</span><strong>${stats.ready || 0}</strong><small>${t('Hazır ancak yayınlanmamış', 'Ready but not published')}</small></article>
        <article class="metric"><span>${t('Planlanan', 'Planned')}</span><strong>${stats.planned || 0}</strong><small>${t('Henüz build edilmemiş', 'Not yet built')}</small></article>
      </section>

      <section class="panel" id="routing-config">
        <div class="panel-heading"><div><h2>${t('Tenant ve Azure Rotası', 'Tenant & Azure Routing')}</h2><p>${brand ? escapeHtml(brand.id) : ''} (${t('Alt etki alanı ve slug eşlemeleri', 'Subdomain & slug mappings')})</p></div></div>
        
        <div class="callout-banner" role="alert" style="background: #f3f4f6; border-left: 4px solid #2563eb; padding: 1rem 1.25rem; margin-bottom: 1.25rem;">
          <strong>${t('Aktif Tenant Base URL:', 'Active Tenant Base URL:')}</strong> 
          <code style="display: block; background: #ffffff; padding: 0.5rem 0.75rem; border: 1px solid #e5e7eb; margin-top: 0.5rem;">${escapeHtml(effectiveBaseUrl)}</code>
        </div>
        
        <form id="routing-form" class="form-grid">
          <div class="field wide"><label for="tenant-primary-domain">${t('Birincil etki alanı', 'Primary domain')}</label><input id="tenant-primary-domain" type="url" required value="${escapeHtml(brand?.routing?.primaryDomain || '')}" placeholder="easeout.net"></div>
          <div class="field wide"><label for="tenant-additional-domains">${t('Ek etki alanları', 'Additional domains')}</label><input id="tenant-additional-domains" type="text" value="${escapeHtml(brand?.routing?.additionalDomains?.join(', ') || '')}" placeholder="www.easeout.net, easeout.pagix.ch"><small>${t('Virgülle ayırın', 'Comma-separated')}</small></div>
          <div class="field"><label for="tenant-base-path">${t('Temel yol', 'Base path')}</label><input id="tenant-base-path" type="text" required value="${escapeHtml(brand?.routing?.basePath || '/lp')}" placeholder="/lp"></div>
          <div class="field"><label for="tenant-backend-port">${t('Backend port', 'Backend port')}</label><input id="tenant-backend-port" type="number" required value="${brand?.routing?.backendPort || 8082}" min="1" max="65535"></div>
          <div class="field wide"><label for="tenant-slug-aliases">${t('Slug eşleştirmeleri', 'Slug aliases')}</label><textarea id="tenant-slug-aliases" rows="4" placeholder="hr-managers: hr-manager-people-ops">${escapeHtml(Object.entries(brand?.routing?.slugAliases || {}).map(([k, v]) => `${k}: ${v}`).join('\n'))}</textarea><small>${t('Satır başına bir eşleşme: eski-slug: yeni-slug', 'One mapping per line: old-slug: new-slug')}</small></div>
          <div class="form-actions"><fluent-button id="save-routing" appearance="accent" type="button">${t('Rotayı Kaydet', 'Save Routing')}</fluent-button></div>
        </form>
      </section>

      <section class="panel" id="pages-catalog">
        <div class="panel-heading" style="display:flex; justify-content:space-between; align-items:center;">
          <div>
            <h2>${t('Landing Pages Catalog', 'Landing Pages Catalog')}</h2>
            <p>${t('Sayfaları filtreleyin, önizleyin veya içeriğini düzenleyin.', 'Filter pages, preview or edit copy.')}</p>
          </div>
          <fluent-button id="add-custom-page" appearance="outline" style="display:none;">+ ${t('Özel sayfa ekle', 'Add custom page')}</fluent-button>
        </div>
        <div class="toolbar" style="display:flex; flex-wrap:wrap; gap:0.75rem; align-items:end; margin-bottom:1.25rem;">
          <div class="field" style="flex:1 1 16rem;"><label for="pages-search">${t('Ara', 'Search')}</label><input id="pages-search" type="search" placeholder="${t('Page ID, segment veya slugh ara', 'Search page ID, segment or slug')}"></div>
          <div class="field" style="flex:1 1 12rem;"><label for="pages-status-filter">${t('Durum Filtresi', 'Status Filter')}</label><select id="pages-status-filter">${statusOptions.map(s => `<option value="${escapeHtml(s)}"${s === 'All' ? ' selected' : ''}>${t(s, s)}</option>`).join('')}</select></div>
          <div class="field" style="flex:1 1 10rem;"><label for="pages-tier-filter">${t('Tier Filtresi', 'Tier Filter')}</label><select id="pages-tier-filter"><option value="all">${t("Tüm Tier'lar", 'All Tiers')}</option><option value="T1">T1</option><option value="T2">T2</option><option value="T3">T3</option></select></div>
        </div>

        <div class="data-wrap">
          <table><thead><tr><th>${t('Page ID', 'Page ID')}</th><th>${t('Segment / Slug', 'Segment / Slug')}</th><th>${t('Hedef Kitle', 'Audience Hook')}</th><th>${t('Tier', 'Tier')}</th><th>${t('Dil', 'Language')}</th><th>${t('Varyant', 'Variant')}</th><th>${t('Durum', 'Status')}</th><th>${t('Canlı Web Linki (Effective URL)', 'Effective Web Link')}</th><th></th></tr></thead><tbody>
            ${pages?.length ? pages.map((p) => {
              const badge = statusBadgeClass(p.status);
              // Compute effective URL using priority: p.effectiveUrl || p.liveUrl || constructed
              const effectiveUrl = p.effectiveUrl || p.liveUrl || (`https://${(brand?.routing?.primaryDomain || brand?.domain || '').replace(/^https?:\/\//, '')}${brand?.routing?.basePath || '/lp'}/${p.lang || ''}/${p.slug || ''}/`);
              
              // Alias URLs
              const aliasUrls = p.aliasUrls || [];
              const hasAliases = Array.isArray(aliasUrls) && aliasUrls.length > 0;
              const linksHtml = `
                <div style="display:flex; flex-direction:column; gap:0.25rem;">
                  <a class='text-link effective-link' href='${escapeHtml(effectiveUrl)}' target='_blank' rel='noreferrer' style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; word-break: break-all;">
                    ${escapeHtml(effectiveUrl)} ↗
                  </a>
                  ${hasAliases ? `
                    <div style="margin-top: 0.25rem; font-size: 0.75rem; color: #6b7280;">
                      <span>${t('Alias:', 'Alias:')}</span>
                      ${aliasUrls.map(url => `
                        <a class='text-link effective-link' href='${escapeHtml(url)}' target='_blank' rel='noreferrer' style="font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.78rem; margin-left: 0.25rem;">
                          ${escapeHtml(url)} ↗
                        </a>
                      `).join('')}
                    </div>
                  ` : ''}
                </div>
              `;
              
              return `<tr>
                <td><code>${escapeHtml(p.pageId || '')}</code></td>
                <td><strong>${escapeHtml(p.slug || '')}</strong></td>
                <td><small>${escapeHtml(p.audienceHook || '')}</small></td>
                <td>${escapeHtml(p.tier || '')}</td>
                <td>${escapeHtml(p.lang || '')}</td>
                <td>${escapeHtml(p.variant || '')}</td>
                <td><span class="status" data-status="${escapeHtml(badge.status)}">${escapeHtml(badge.label)}</span></td>
                <td>${linksHtml}</td>
                <td><div class="row-actions">
                  <fluent-button appearance="subtle" data-preview-page="${escapeHtml(p.slug)}">${t('Önizle', 'Preview')}</fluent-button>
                  <fluent-button appearance="subtle" data-edit-copy="${escapeHtml(p.slug)}">${t('İçeriği Düzenle', 'Edit Copy')}</fluent-button>
                </div></td>
              </tr>`;
            }).join('') : `<tr><td colspan="9" class="empty">${t('Henüz sayfa yok.', 'No pages yet.')}</td></tr>`}
          </tbody></table>
        </div>
      </section>
    `;

    // Routing save button
    const saveRoutingBtn = document.querySelector('#save-routing');
    const formInputs = () => {
      const aliasesRaw = document.querySelector('#tenant-slug-aliases').value.trim().split('\n').filter(Boolean);
      const aliases = {};
      aliasesRaw.forEach(line => {
        const [k, ...rest] = line.split(':');
        if (k) aliases[k.trim()] = rest.join(':').trim();
      });
      return {
        primaryDomain: document.querySelector('#tenant-primary-domain').value.trim(),
        additionalDomains: document.querySelector('#tenant-additional-domains').value.split(',').map(s => s.trim()).filter(Boolean),
        basePath: document.querySelector('#tenant-base-path').value.trim(),
        backendPort: Number(document.querySelector('#tenant-backend-port').value),
        slugAliases: aliases,
      };
    };

    if (saveRoutingBtn) {
      saveRoutingBtn.onclick = async () => {
        const domain = document.querySelector('#tenant-primary-domain').value.trim();
        if (!domain) return showNotice(t('Birincil etki alanı zorunludur.', 'Primary domain is required.'), 'error');
        try {
          await api(`/api/tenants/${encodeURIComponent(brand?.id || '')}/routing`, {
            method: 'PUT',
            body: formInputs(),
          });
          showNotice(t('Tenant rotası kaydedildi.', 'Tenant routing saved.'), 'success');
          // Trigger overview refresh for UI reflect
          await refreshMeta();
        } catch (err) {
          showNotice(err.message, 'error');
        }
      };
    }

    // Rebuild/Publish button
    const rebuildBtn = document.querySelector('#rebuild-tenant');
    if (rebuildBtn) {
      rebuildBtn.onclick = async () => {
        if (!state.brandId) return showNotice(t('Önce bir marka seçin.', 'Select a brand first.'), 'error');
        try {
          await startJob('build', { brandId: state.brandId });
          showNotice(t('Tenant rebuild başlatıldı.', 'Tenant rebuild started.'), 'success');
        } catch (err) {
          showNotice(err.message, 'error');
        }
      };
    }

    // Filters
    const searchInput = document.querySelector('#pages-search');
    const statusFilter = document.querySelector('#pages-status-filter');
    const tierFilter = document.querySelector('#pages-tier-filter');

    const filterTable = () => {
      const q = (searchInput.value || '').toLowerCase().trim();
      const st = statusFilter.value;
      const tr = tierFilter.value;
      document.querySelectorAll('#pages-catalog tbody tr').forEach(row => {
        const pid = row.children[0].textContent.toLowerCase();
        const slug = row.children[1].textContent.toLowerCase();
        const hook = row.children[2].textContent.toLowerCase();
        const tier = row.children[3].textContent;
        const status = row.children[6].querySelector('.status')?.dataset?.status || '';
        const matchesQ = !q || pid.includes(q) || slug.includes(q) || hook.includes(q);
        const matchesSt = st === 'All' || status === st.toLowerCase();
        const matchesTr = tr === 'all' || tier === tr;
        row.style.display = (matchesQ && matchesSt && matchesTr) ? '' : 'none';
      });
    };

    searchInput?.addEventListener('input', filterTable);
    statusFilter?.addEventListener('change', filterTable);
    tierFilter?.addEventListener('change', filterTable);

    // Actions
    document.querySelectorAll('[data-preview-page]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const slug = btn.dataset.previewPage;
        const page = pages?.find(p => p.slug === slug);
        if (!page) return showNotice(t('Sayfa bulunamadı.', 'Page not found.'), 'error');
        if (page.status !== 'Published') return showNotice(t('Sadece yayınlanmış sayfalar önizlenebilir.', 'Only published pages are previewable.'), 'error');
        const liveUrl = page.effectiveUrl || page.liveUrl || ('https://' + (brand?.routing?.primaryDomain || brand?.domain || '').replace(/^https?:\/\//, '') + (brand?.routing?.basePath || '/lp') + '/' + (page.lang || 'en') + '/' + slug + '/');
        window.open(liveUrl, '_blank');
      });
    });

    document.querySelectorAll('[data-edit-copy]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const slug = btn.dataset.editCopy;
        const page = pages?.find(p => p.slug === slug);
        if (!page) return showNotice(t('Sayfa bulunamadı.', 'Page not found.'), 'error');

        // Open content editor in admin with slug-based search
        // First switch to content section
        await navigate('content');

        // Then select the matching combo or search for it
        setTimeout(() => {
          const search = document.querySelector('#content-search');
          if (search) {
            search.value = slug;
            search.dispatchEvent(new Event('input', { bubbles: true }));
          }
          // Attempt to highlight the row matching this slug
          const buttons = document.querySelectorAll('[data-content-index]');
          buttons.forEach(btn => {
            if (btn.dataset.search && btn.dataset.search.includes(slug)) {
              btn.click();
            }
          });
        }, 400);
      });
    });

    const addCustomBtn = document.querySelector('#add-custom-page');
    if (addCustomBtn) {
      addCustomBtn.onclick = () => showNotice(t('Özel sayfa ekleme şimdilik devre dışı.', 'Add custom page is currently disabled.'), 'warning');
    }

  } catch (err) {
    content.innerHTML = `<div class="panel"><h1>${t('HOSTING GÖRÜNÜMÜ YÜKLENEMEDİ', 'HOSTING VIEW FAILED')}</h1><p>${escapeHtml(err.message)}</p></div>`;
    showNotice(err.message, 'error');
  }
}

// Optional helper if host doesn't provide heading/brandOptions/...
// kept local for safety — but prefer host provided helpers above
function localizeHeading(titleEn, titleTr, descEn, descTr) {
  const currentLang = localStorage.getItem('pagix-admin-lang') || localStorage.getItem('landcraft-admin-lang') || (navigator.language.startsWith('tr') ? 'tr' : 'en');
  const t = (trVal, enVal) => currentLang === 'tr' ? trVal : enVal;
  const Heading = (title, description, actions = '') => `<header class="page-heading"><div><h1>${title}</h1><p>${description}</p></div><div class="actions">${actions}</div></header>`;
  return Heading(t(titleTr, titleEn), t(descTr, descEn));
}
